import React from 'react';
import { Image, Platform, StyleSheet, View } from 'react-native';
import { colorFor } from '../../../utils/itemPalette';
import type { ImageTransform } from '../useImageTransform';

/**
 * Absolute-positioned pixel-mask overlays per inventory item. One
 * `<img>` per mask, rendered at 0.55 opacity over the room photo.
 *
 * Two important safety rules:
 *
 *  1. **Allowlist the origin.** Only render signed GCS URLs from
 *     `storage.googleapis.com`. Anything else (a compromised backend
 *     pivot vector) gets silently dropped. Server is the primary
 *     gate; this is defence in depth.
 *
 *  2. **Rectangle masks suppress the tint fill.** When
 *     `segmentation_model === 'user_box'` the PNG IS the entire
 *     bbox rectangle — tinting it at 0.55 alpha hazes the photo
 *     out once 3+ such masks stack. The bbox outline (rendered by
 *     BboxLayer) is enough visual cue. Silhouette masks from SAM3
 *     continue to render normally.
 *
 * Web-specific: `crossorigin="anonymous"` + `referrerpolicy="no-
 * referrer"` keep the request cachable + don't leak the current
 * page URL to Google. React Native Web passes these through.
 */

export interface MaskLayerItem {
  id: number;
  /** NULL on the Gemini native-bbox path — render is skipped; the
   *  bbox rectangle from BboxLayer is the whole signal. */
  mask_url: string | null;
  model:
    | 'sam3'
    | 'user_box'
    | 'user_box_fallback'
    | 'gemini_native_bbox'
    | string;
  selected: boolean;
}

interface MaskLayerProps {
  transform: ImageTransform;
  items: MaskLayerItem[];
}

const isAllowedMaskUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return (
      u.protocol === 'https:' &&
      u.hostname.endsWith('storage.googleapis.com')
    );
  } catch {
    return false;
  }
};

export const MaskLayer: React.FC<MaskLayerProps> = ({ transform, items }) => {
  if (!transform.ready || !transform.renderRect) return null;
  const { x, y, w, h } = transform.renderRect;
  return (
    <View
      pointerEvents="none"
      style={[
        StyleSheet.absoluteFill,
        // Clip masks to the image's actual rendered rect so they
        // don't bleed into letterboxing.
        { overflow: 'hidden' },
      ]}
    >
      <View
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width: w,
          height: h,
        }}
      >
        {items.map((it) => {
          if (!isAllowedMaskUrl(it.mask_url)) return null;
          // Suppress tint-fill on rectangle masks (see note above).
          if (it.model === 'user_box') return null;
          const palette = colorFor(it.id);
          return (
            <View
              key={`mask-${it.id}`}
              style={[
                StyleSheet.absoluteFill,
                {
                  opacity: it.selected ? 0.55 : 0.28,
                  ...(Platform.OS === 'web'
                    ? ({
                        // Tinted silhouette: we want the mask shape
                        // to read as the palette color. Image
                        // rasterises black-and-white by convention;
                        // mix-blend + tinted overlay gives the hue.
                        mixBlendMode: 'screen',
                      } as any)
                    : null),
                },
              ]}
            >
              <Image
                source={{ uri: it.mask_url }}
                style={[
                  StyleSheet.absoluteFill,
                  {
                    width: '100%',
                    height: '100%',
                    ...(Platform.OS === 'web'
                      ? ({
                          // Tint the mask shape by multiplying the
                          // alpha channel against the palette color.
                          // `filter` is a web-only CSS property.
                          filter: `drop-shadow(0 0 0 ${palette.solid})`,
                        } as any)
                      : null),
                  },
                ]}
                resizeMode="stretch"
                {...(Platform.OS === 'web'
                  ? ({
                      crossOrigin: 'anonymous',
                      referrerPolicy: 'no-referrer',
                    } as any)
                  : {})}
              />
            </View>
          );
        })}
      </View>
    </View>
  );
};
